import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const WelcomeGuide = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const steps = [
    {
      title: "🎉 Welcome to Smart Email Verifier!",
      subtitle: "Your complete email verification solution",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Professional Email Verification Platform</h2>
            <p className="text-gray-600">Clean your email lists, improve deliverability, and boost your email marketing success.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Core Features
              </h3>
              <ul className="text-blue-700 space-y-2 text-sm">
                <li>• <strong>Single Email Validation</strong> - Check individual emails instantly</li>
                <li>• <strong>Bulk Email Validation</strong> - Process thousands of emails</li>
                <li>• <strong>Real-time Dashboard</strong> - Monitor progress and results</li>
                <li>• <strong>Advanced Analytics</strong> - Detailed reports and insights</li>
                <li>• <strong>Export Results</strong> - Download in multiple formats</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Benefits
              </h3>
              <ul className="text-green-700 space-y-2 text-sm">
                <li>• <strong>Reduce Bounce Rates</strong> - Remove invalid emails</li>
                <li>• <strong>Improve Deliverability</strong> - Better sender reputation</li>
                <li>• <strong>Save Costs</strong> - Focus on valid subscribers</li>
                <li>• <strong>Compliance Ready</strong> - GDPR and CAN-SPAM compliant</li>
                <li>• <strong>High Accuracy</strong> - Advanced verification methods</li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-yellow-800 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Quick Start
            </h3>
            <p className="text-yellow-700 text-sm">
              This guide will walk you through all the essential features. You can skip at any time and explore on your own!
            </p>
          </div>
        </div>
      ),
      action: "Next: Single Email Verification",
      actionType: "next"
    },
    {
      title: "📧 Single Email Verification",
      subtitle: "Verify individual email addresses instantly",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              How to verify a single email:
            </h3>
            <ol className="text-blue-700 space-y-3 text-sm">
              <li className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                <span>Go to the <strong>Single Validation</strong> page from the sidebar</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                <span>Enter the email address you want to verify</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                <span>Click <strong>"Verify Email"</strong> button</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                <span>View detailed results including verification methods used</span>
              </li>
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">✅ Valid</h3>
              <p className="text-green-700 text-sm">
                Email exists and can receive messages. Safe to send emails to.
              </p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-2">❌ Invalid</h3>
              <p className="text-red-700 text-sm">
                Email doesn't exist. Remove from your list to avoid bounces.
              </p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="font-semibold text-orange-800 mb-2">⚠️ Catch All</h3>
              <p className="text-orange-700 text-sm">
                Domain accepts all emails. Verification inconclusive - use with caution.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-2">❓ Unknown</h3>
              <p className="text-gray-700 text-sm">
                Unable to determine status. May require manual verification.
              </p>
            </div>
          </div>
        </div>
      ),
      action: "Next: Bulk Verification",
      actionType: "next"
    },
    {
      title: "📊 Bulk Email Verification",
      subtitle: "Upload and verify email lists efficiently",
      content: (
        <div className="space-y-6">
          <div className="bg-purple-50 p-5 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              CSV File Requirements:
            </h3>
            <ul className="text-purple-700 space-y-2 text-sm">
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                First column must be named <strong>"email"</strong> (lowercase)
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Additional columns can be named anything
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Maximum file size: 10MB
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Supported formats: CSV, TXT
              </li>
            </ul>
          </div>

          <div className="bg-indigo-50 p-5 rounded-lg border border-indigo-200">
            <h3 className="font-semibold text-indigo-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Bulk verification process:
            </h3>
            <ol className="text-indigo-700 space-y-3 text-sm">
              <li className="flex items-start">
                <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                <span>Prepare your CSV file with email column first</span>
              </li>
              <li className="flex items-start">
                <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                <span>Go to <strong>List Validation</strong> page from sidebar</span>
              </li>
              <li className="flex items-start">
                <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                <span>Click <strong>"Upload List"</strong> and select your file</span>
              </li>
              <li className="flex items-start">
                <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                <span>Monitor progress in real-time on the dashboard</span>
              </li>
              <li className="flex items-start">
                <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">5</span>
                <span>Download results when complete</span>
              </li>
            </ol>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-yellow-800 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Pro Tip
            </h3>
            <p className="text-yellow-700 text-sm">
              For large lists, the verification runs in the background. You can close the browser and check back later - your progress will be saved!
            </p>
          </div>
        </div>
      ),
      action: "Next: Dashboard & Results",
      actionType: "next"
    },
    {
      title: "📈 Dashboard & Results",
      subtitle: "Monitor progress and analyze results",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Dashboard Overview
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">Statistics Cards</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• Total Jobs - All verification jobs</li>
                  <li>• Valid Emails - Successfully verified</li>
                  <li>• Invalid Emails - Non-existent emails</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">Charts & Tables</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• Status Distribution Pie Chart</li>
                  <li>• Recent Jobs Table</li>
                  <li>• Real-time Progress Updates</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-5 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export & Download
            </h3>
            <ul className="text-green-700 space-y-2 text-sm">
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Download results in CSV format
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Filter by status (Valid, Invalid, Catch All, etc.)
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Include original data columns
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Detailed verification methods used
              </li>
            </ul>
          </div>


        </div>
      ),
      action: "Next: Advanced Settings",
      actionType: "next"
    },
    {
      title: "⚙️ Advanced Settings & Configuration",
      subtitle: "Optimize performance and customize verification behavior",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Concurrency & Performance Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">SMTP Concurrency</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>Default: 5</strong> - Simultaneous SMTP connections</li>
                  <li>• <strong>Range: 1-20</strong> - Adjust based on server capacity</li>
                  <li>• <strong>Higher = Faster</strong> but may trigger rate limits</li>
                  <li>• <strong>Lower = Safer</strong> for sensitive domains</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">Upload Concurrency</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>Import Concurrency</strong> - Files processed at once</li>
                  <li>• <strong>Worker Concurrency</strong> - Background job workers</li>
                  <li>• <strong>Max Upload Size</strong> - File size limit</li>
                  <li>• <strong>Max Email List Size</strong> - Emails per file</li>
                  <li>• <strong>All customizable</strong> in Settings page</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-5 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Rate Limiting & Cooldowns
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Provider-Specific Limits</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• <strong>Normal Cooldown</strong> - 1 second between emails</li>
                  <li>• <strong>Yahoo/AOL</strong> - 2 hours between batches</li>
                  <li>• <strong>Microsoft</strong> - 5 minutes between batches</li>
                  <li>• <strong>Retry Attempts</strong> - 3 attempts with delays</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Smart Rate Limiting</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• <strong>Automatic Detection</strong> - Detects provider limits</li>
                  <li>• <strong>Progressive Delays</strong> - Increases delays on errors</li>
                  <li>• <strong>Batch Processing</strong> - Groups emails by domain</li>
                  <li>• <strong>Error Recovery</strong> - Resumes after cooldowns</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 p-5 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
              SMTP Configuration & EHLO/HELO
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-purple-700 mb-2">Connection Settings</h4>
                <ul className="text-purple-600 space-y-1">
                  <li>• <strong>SMTP Host</strong> - Your email server</li>
                  <li>• <strong>SMTP Port</strong> - Usually 587 (TLS) or 465 (SSL)</li>
                  <li>• <strong>Timeout</strong> - Connection timeout (10 seconds)</li>
                  <li>• <strong>Secure</strong> - Enable SSL/TLS encryption</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-purple-700 mb-2">EHLO/HELO Configuration</h4>
                <ul className="text-purple-600 space-y-1">
                  <li>• <strong>HELO Domain</strong> - Your server's hostname</li>
                  <li>• <strong>SMTP Command</strong> - EHLO or HELO greeting</li>
                  <li>• <strong>User Agent</strong> - Custom identification</li>
                  <li>• <strong>Authentication</strong> - Username/password</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 p-5 rounded-lg border border-orange-200">
            <h3 className="font-semibold text-orange-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Email Scanner & Verification Methods
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-orange-700 mb-2">Scanner Settings</h4>
                <ul className="text-orange-600 space-y-1">
                  <li>• <strong>Scan From</strong> - Sender email address</li>
                  <li>• <strong>SMTP Port</strong> - Target port (usually 25)</li>
                  <li>• <strong>Timeout</strong> - Scanner timeout (15 seconds)</li>
                  <li>• <strong>Batch Size</strong> - Emails per batch (50)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-orange-700 mb-2">Verification Methods</h4>
                <ul className="text-orange-600 space-y-1">
                  <li>• <strong>SMTP Probe</strong> - Direct server connection</li>
                  <li>• <strong>MX Record Check</strong> - DNS validation</li>
                  <li>• <strong>Catch-all Detection</strong> - Domain analysis</li>
                  <li>• <strong>IMAP Verification</strong> - Advanced checking</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-yellow-800 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Configuration Tips
            </h3>
            <ul className="text-yellow-700 space-y-2 text-sm">
              <li>• <strong>Start Conservative</strong> - Begin with lower concurrency and increase gradually</li>
              <li>• <strong>Monitor Logs</strong> - Check for rate limiting and errors</li>
              <li>• <strong>Test Settings</strong> - Use test emails before bulk verification</li>
              <li>• <strong>Provider Limits</strong> - Respect email provider rate limits</li>
              <li>• <strong>Security First</strong> - Use secure connections and strong passwords</li>
            </ul>
          </div>
        </div>
      ),
      action: "Get Started Now!",
      actionType: "finish"
    }
  ];

  const handleAction = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
      navigate('/dashboard');
    }
  };

  const handleSkip = () => {
    onClose();
    navigate('/dashboard');
  };

  // Lock body scroll when modal is open
  React.useEffect(() => {
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  return (
    <div className="modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 relative z-10">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">{steps[currentStep].title}</h1>
              <p className="text-blue-100 mt-1">{steps[currentStep].subtitle}</p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-blue-100">
                {currentStep + 1} of {steps.length}
              </span>
              <button
                onClick={handleSkip}
                className="text-blue-100 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4 bg-blue-200 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {steps[currentStep].content}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center relative z-10">
          <button
            onClick={handleSkip}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            Skip Guide
          </button>

          <div className="flex space-x-3">
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Previous
              </button>
            )}
            <button
              onClick={handleAction}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              {steps[currentStep].action}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeGuide; 